/* eslint-disable indent */
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FieldValues } from 'react-hook-form';
import { useCropVideo } from '@/hooks/crop-video.hooks';
import { getSortedList } from '@/hooks/list-order';
import {
  useUpdateResults,
  useUploadVehiculeCheckResultImage,
} from '@/hooks/vehicule-check-result.hooks';
import { useModerate } from '@/hooks/moderation.hooks';
import { useImageNavigation } from '@/hooks/image-navigations.hooks';
import { SingleIconButton } from '@/components/Custom/Button/SingleIconButton';
import { PlusIcon } from '@/components/Custom/Icons/PlusIcon';
import { Popover } from '@/components/Popover';
import { NotFound } from '@/components/Message/NotFound';
import Loader from '@/components/Loading/Loader';
import { ScreenAddIcon } from '@/components/SvgIcons/ScreenAddIcon';
import { PhotoWithMenu } from '@/components/Custom/Photo/PhotoWithMenu';
import { ResultField } from './ResultField';
import { TEquipmentSchema } from '@/schemas/equipment/equipment.schema';
import { TDamageSchema } from '@/schemas/damage/damage.schema';
import { TVehiculeCheckResultSchema } from '@/schemas/vehicule-check-result/vehicule-check-result.schema';
import { showModal } from '@/utils/modal.utils';
import {
  addResultImage,
  editResultImage,
  getRelatedObjectPayloads,
} from '../utils/result-image.utils';
import { ResultImageModal } from '@/components/Custom/Modal/ResultModal/ResultImageModal';
import { useAddOrUpdateUrlParam } from '@/hooks/url-search-params.hooks';
import { ResultActionState } from '@/api/entities/vehicule-check-result';
import {
  ResultModalStep,
  ResultPhotoTab,
} from '@/type/ui/tab/vehiculeCheckResultTab';
import 'react-image-crop/dist/ReactCrop.css';
import ConfirmationModal from '@/components/Custom/Modal/ConfirmationModal';
import { TResultObjectSchema } from '@/schemas/vehicule-check-result/result-object.schema';
import { TFunction } from 'i18next';
import { useUserRole } from '@/hooks/auth.hooks';
import { ResultPhotoLegend } from './ResultPhotoLegend';
import { ArrowDirection } from '@/api/entities/vehicule-check-type-check-point';
import { ModerationReportCanvas } from '@/components/Custom/ModerationReportCanvas';
import { MODE_DRAW, R2_PUBLIC_DOMAIN } from '@/utils/constant.utils';
import { ImageNavigations } from '@/components/ImageNavigations';
import { ImagePreviewModal } from '@/components/Custom/Modal/ImagePreviewModal';
import { Repair } from '@/entities/repair';
import { getRepairOptions } from '../utils/result-details.utils';

interface ResultPhotoListProps<TSchema extends FieldValues> {
  t?: TFunction<undefined>;
  activeTab?: ResultPhotoTab;
  checkPointId?: string;
  checkPointDirection?: ArrowDirection;
  vehiculeCheckResult?: TVehiculeCheckResultSchema;
  hideAddImage?: boolean;
  data: TSchema[] | undefined;
  isLoadingRepairData?: boolean;
  repairData?: Repair[];
  title: string;
  notFoundText: string;
  activateEngineCorrection?: boolean;
  hidePhotoActionButtons?: boolean;
  isModerator?: boolean;
  moderationStatus: string;
  editAllow?: boolean;
  imagePreviewModalId?: string;
}

export const ResultPhotoList = <TSchema extends FieldValues>({
  t,
  activeTab,
  checkPointId,
  checkPointDirection,
  vehiculeCheckResult,
  hideAddImage,
  data,
  isLoadingRepairData,
  repairData,
  title,
  notFoundText,
  activateEngineCorrection = false,
  hidePhotoActionButtons = false,
  isModerator,
  moderationStatus,
  editAllow,
  imagePreviewModalId = 'result-photo-preview-modal',
}: ResultPhotoListProps<TSchema>) => {
  const { getNewUrl } = useAddOrUpdateUrlParam();
  const navigate = useNavigate();
  const {
    setActivateModeration,
    activateModeration,
    element,
    dmg,
    setCurrentItemEdit,
    setElement,
    setDmg,
  } = useModerate();
  const { isAdmin, isGroupManager } = useUserRole();
  const actionRef = useRef<ResultActionState>();
  const [selectedResultObject, setSelectedResultObject] =
    useState<TResultObjectSchema>();
  const [selectedEquipment, setSelectedEquipment] =
    useState<TEquipmentSchema>();
  const [selectedDamage, setSelectedDamage] = useState<TDamageSchema>();
  const [canvasImageLoading, setCanvasImageLoading] = useState(true);

  const [isImagePreviewModalOpen, setIsImagePreviewModalOpen] = useState(false);

  const { getImageNavigationHandlers, navigateToLastImage } =
    useImageNavigation();

  const countActiveImages = data
    ?.map((item) => item?.images?.filter((image) => !image?.isDeleted)?.length)
    ?.reduce((acc, curr) => acc + curr, 0);

  const containsImageArray = data?.some((item) => Array.isArray(item?.images));

  const orderBy = data?.['order'] ? 'order' : 'updatedAt';
  const orderDirection = data?.['order'] ? 'asc' : 'desc';

  const filteredData = containsImageArray
    ? getSortedList(data, orderBy, orderDirection)?.filter((item) => {
        const activeImages =
          item?.images?.filter((image) => !image?.isDeleted) || [];
        return activeImages.length > 0;
      })
    : getSortedList(data, orderBy, orderDirection);

  const {
    videoRef,
    cropConfigs,
    setCropConfigs,
    setCompletedCrop,
    getCroppedImage,
    resetCrop,
  } = useCropVideo();

  const { mutate: mutateUploadImage, isPending: isPendingUploadImage } =
    useUploadVehiculeCheckResultImage({
      handleSuccess: (data: {
        data: {
          filename: string;
        };
      }) => {
        if (actionRef.current === ResultActionState.ADD) {
          addResultImage({
            activeImageTab: activeTab,
            vehiculeCheckResult,
            selectedEquipment,
            selectedDamage,
            fileName: data.data.filename,
            checkPointId,
            coordinatesModerated:
              activeTab === ResultPhotoTab.DAMAGES ? dmg : element,
            canModerate: isModerator || isAdmin || isGroupManager,
            moderationStatus,
            mutate: mutateUpdateImage,
          });
        }

        if (actionRef.current === ResultActionState.EDIT) {
          editResultImage({
            activeImageTab: activeTab,
            vehiculeCheckResult,
            selectedEquipment:
              selectedEquipment || selectedResultObject?.equipment,
            selectedDamage: selectedDamage || selectedResultObject?.damage,
            fileName: data.data.filename,
            checkPointId,
            addImage: selectedResultObject?.['addImage'],
            selectedResultObjectId: selectedResultObject?.id,
            currentImageIndex: selectedResultObject?.['currentImageIndex'],
            coordinatesModerated:
              selectedResultObject?.coordinatesModerated ||
              (activeTab === ResultPhotoTab.DAMAGES ? dmg : element),
            canModerate: isModerator || isAdmin || isGroupManager,
            activateModeration,
            moderationStatus,
            mutate: mutateUpdateImage,
          });
        }
      },
    });

  const { mutate: mutateUpdateImage, isPending: isPendingUpdateImage } =
    useUpdateResults({
      handleSuccess: () => {
        setElement(undefined);
        setDmg(undefined);
        setSelectedEquipment(undefined);
        setSelectedDamage(undefined);

        if (selectedResultObject?.['addImage']) {
          navigateToLastImage(selectedResultObject?.id);
        }
      },
    });

  const onCanvasImageLoading = (loading: boolean) => {
    setCanvasImageLoading(loading);
  };

  const onSelectEquipment = (equipment: TEquipmentSchema | undefined) => {
    const payloads = getRelatedObjectPayloads(equipment);
    setSelectedEquipment(payloads);
  };

  const onSelectDamage = (damage: TDamageSchema | undefined) => {
    const payloads = getRelatedObjectPayloads(damage);
    setSelectedDamage(payloads);
  };

  const onClickAdd = () => {
    const newUrl = getNewUrl({
      key: 'step',
      value: ResultModalStep.SELECT_ENTITY,
    });
    navigate(newUrl);
    actionRef.current = ResultActionState.ADD;
    resetCrop();
  };

  const onClickEdit = (step: ResultModalStep, item: TResultObjectSchema) => {
    const newUrl = getNewUrl({
      key: 'step',
      value: step,
    });
    navigate(newUrl);
    actionRef.current = ResultActionState.EDIT;
    setSelectedResultObject({
      ...item,
      image: item?.['addImage'] ? undefined : item?.image,
      customImage: item?.['addImage'] ? undefined : item?.customImage,
      coordinatesModerated: item?.['addImage']
        ? undefined
        : item?.coordinatesModerated,
    });

    // Show the modal after setting all necessary state
    showModal('#result-image-modal');
  };

  const onClickDeactivate = () => {
    editResultImage({
      activeImageTab: activeTab,
      vehiculeCheckResult,
      checkPointId,
      selectedEquipment: selectedResultObject?.equipment,
      selectedDamage: selectedResultObject?.damage,
      selectedResultObjectId: selectedResultObject?.id,
      currentImageIndex: selectedResultObject?.['currentImageIndex'],
      coordinatesModerated:
        selectedResultObject?.coordinatesModerated ||
        (activeTab === ResultPhotoTab.DAMAGES ? dmg : element),
      removeCoordinates: selectedResultObject?.['removeCoordinates'],
      canModerate: isModerator || isAdmin || isGroupManager,
      activateModeration,
      moderationStatus,
      deactivateSingleImage: selectedResultObject?.['deactivateSingleImage'],
      deactivateAllImages: selectedResultObject?.['deactivateAllImages'],
      mutate: mutateUpdateImage,
    });
  };

  return (
    <>
      <div className="flex items-center mb-5 gap-3">
        <ResultField
          containerClassName="p-0 bg-transparent"
          textClassName="text-md text-black font-medium"
          text={title}
        />
        {!hideAddImage && (
          <div className="flex items-center gap-3">
            {(isAdmin || isGroupManager) && (
              <>
                <div className="text-2sm text-gray-600">|</div>
                <div className="text-2sm text-gray-700">
                  {t('common:addImage')}
                </div>
                <SingleIconButton
                  icon={PlusIcon}
                  disabled={!editAllow}
                  dataModalToggle="#result-image-modal"
                  buttonClassName="flex justify-center items-center w-9 min-w-9 h-9 min-h-9 !bg-blue-2948FF rounded-full shadow-xl"
                  onClick={() => {
                    onClickAdd();
                    setActivateModeration(false);
                  }}
                />
              </>
            )}
            {activateEngineCorrection && (
              <>
                <div className="text-2sm text-black">|</div>
                <div className="text-2sm text-gray-700">
                  {t('common:addModeration')}
                </div>
                <SingleIconButton
                  icon={ScreenAddIcon}
                  disabled={!editAllow}
                  dataModalToggle="#result-image-modal"
                  buttonClassName="flex justify-center items-center w-9 min-w-9 h-9 min-h-9 !bg-blue-2948FF rounded-full shadow-xl"
                  iconClassName="[&>path]:fill-white  w-[19px] h-[19px] -mr-[1.27px]"
                  onClick={() => {
                    onClickAdd();
                    setActivateModeration(true);
                  }}
                />
              </>
            )}
          </div>
        )}
      </div>

      {countActiveImages === 0 ? (
        <div className="pt-4 pb-10">
          <NotFound text={notFoundText} />
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-x-6 gap-y-8">
          {filteredData?.map((item) => {
            const isCheckPoint = item?.checkPoint;
            const itemId = isCheckPoint ? item?.checkPoint?.id : item?.id;

            const objectField = activeTab?.substring(0, activeTab?.length - 1);

            const activeImages =
              item?.images?.filter((image) => !image?.isDeleted) || [];

            const {
              containsImageList,
              containsMoreThanOneImage,
              currentImageIndex,
              currentImageUrl,
              currentImageItem,
              onLeftClick,
              onRightClick,
              onDotClick,
            } = getImageNavigationHandlers({
              itemId,
              images: activeImages,
              singleImageUrl: item?.customImage || item?.image,
            });

            // TODO: Uncomment this after temporarily hiding moderation coordinates for ImageList
            const currentCoordinatesModerated = containsImageList
              ? activeImages?.[currentImageIndex]?.coordinatesModerated
              : item?.coordinatesModerated;
            // const currentCoordinatesModerated = containsImageList
            //   ? undefined
            //   : item?.coordinatesModerated;

            const currentItem = containsImageList
              ? {
                  ...item,
                  image: currentImageItem?.url,
                  name: item?.[objectField]?.name,
                  currentImageIndex,
                  coordinatesModerated: currentCoordinatesModerated,
                  originalBoundingBox: item?.original_bbox,
                }
              : {
                  ...item,
                  image: isCheckPoint
                    ? item?.checkPoint?.imageCheckPoint
                    : item?.[objectField]?.image,
                  name: isCheckPoint
                    ? item?.checkPoint?.name
                    : item?.[objectField]?.name,
                  originalBoundingBox: item?.original_bbox,
                };

            const reparation = getRepairOptions(
              item?.damage?.name?.split('_')[0],
              item?.equipment?.name,
              item?.damage?.severity,
              repairData
            );

            return (
              <PhotoWithMenu
                t={t}
                key={itemId}
                url={
                  isCheckPoint
                    ? item?.checkPoint?.imageCheckPoint
                    : currentImageUrl
                }
                name={
                  currentItem?.damage ? (
                    <div className="flex flex-col">
                      {checkPointDirection && (
                        <ResultPhotoLegend
                          fieldText={`${t('common:positioning')} :`}
                          valueText={checkPointDirection}
                        />
                      )}
                      {currentItem?.equipment?.name && (
                        <ResultPhotoLegend
                          fieldText={`${t('common:equipment')} :`}
                          valueText={currentItem?.equipment?.name}
                        />
                      )}
                      {currentItem?.damage?.name && (
                        <ResultPhotoLegend
                          fieldText={`${t('common:damage')} :`}
                          valueText={currentItem?.damage?.name?.split('_')[0]}
                        />
                      )}
                      {currentItem?.damage?.severity && (
                        <ResultPhotoLegend
                          fieldText={`${t('common:severity')} :`}
                          valueText={currentItem?.damage?.severity}
                        />
                      )}
                      <ResultPhotoLegend
                        fieldText={`${t('common:repairs')} :`}
                        valueText={
                          isLoadingRepairData ? (
                            <>{`${t('common:loading')}...`}</>
                          ) : !reparation?.length ? (
                            <div className="-mt-0.5 px-[9px] py-0.75 text-primary text-xs border border-primary rounded-[5px]">
                              {t('common:noData')}
                            </div>
                          ) : (
                            <Popover
                              containerClassName="-mt-0.5"
                              titleClassName="px-[9px] py-1 text-white text-xs bg-primary rounded-[5px]"
                              contentClassName="px-4 py-2"
                              title={t('common:view')}
                            >
                              {reparation.map((rep, index) => (
                                <div className="grid grid-cols-2" key={index}>
                                  <ResultPhotoLegend
                                    fieldText={`${rep.name} :`}
                                    valueText=""
                                  />
                                  <ResultPhotoLegend
                                    containerClassName="justify-center"
                                    fieldText=""
                                    valueText={`${rep.estimatedCost} Euros`}
                                  />
                                </div>
                              ))}
                            </Popover>
                          )
                        }
                      />
                    </div>
                  ) : (
                    currentItem?.name
                  )
                }
                hideActionButtons={hidePhotoActionButtons}
                detectionCoordinates={currentCoordinatesModerated}
                // TODO: Uncomment this after temporarily hiding moderation buttons for ImageList
                activateEngineCorrection={activateEngineCorrection}
                // activateEngineCorrection={
                //   containsImageList ? false : activateEngineCorrection
                // }
                originalBoundingBox={currentItem?.originalBoundingBox}
                isModerator={isModerator}
                timeInVideo=""
                onCanvasImageLoading={onCanvasImageLoading}
                navigationChildren={
                  containsMoreThanOneImage && (
                    <ImageNavigations
                      arrowClassName="border border-white/50"
                      images={activeImages}
                      activeImageId={activeImages?.[currentImageIndex]?.id}
                      disabled={canvasImageLoading}
                      onLeftClick={onLeftClick}
                      onRightClick={onRightClick}
                      onDotClick={onDotClick}
                    />
                  )
                }
                onClickImage={() => {
                  setSelectedResultObject(currentItem);
                  showModal(`#${imagePreviewModalId}`);
                }}
                onClickAdd={
                  containsImageList
                    ? () => {
                        setActivateModeration(false);

                        const editedItem = {
                          ...currentItem,
                          addImage: true,
                        } as TResultObjectSchema;

                        onClickEdit(ResultModalStep.SELECT_ENTITY, editedItem);
                      }
                    : undefined
                }
                onClickEdit={() => {
                  setActivateModeration(false);
                  onClickEdit(ResultModalStep.SELECT_ENTITY, currentItem);
                }}
                onClickDeactivateSingleImage={() => {
                  setSelectedResultObject({
                    ...currentItem,
                    deactivateSingleImage: true,
                  });
                  setActivateModeration(false);
                  showModal('#confirmation-remove-item-modal');
                }}
                onClickDeactivateAllImages={
                  containsMoreThanOneImage
                    ? () => {
                        setSelectedResultObject({
                          ...currentItem,
                          deactivateAllImages: true,
                        });
                        setActivateModeration(false);
                        showModal('#confirmation-remove-item-modal');
                      }
                    : undefined
                }
                onClickEditModeration={() => {
                  setActivateModeration(true);
                  setCurrentItemEdit({
                    id: currentItem?.id,
                    url: `${R2_PUBLIC_DOMAIN}/${
                      currentItem?.customImage || currentItem?.image
                    }`,
                    name: currentItem?.name,
                    coordinatesModerated: currentItem?.coordinatesModerated,
                    currentImageIndex: currentItem?.currentImageIndex,
                  });
                  onClickEdit(ResultModalStep.MODERATE, currentItem);
                }}
                onClickDeactivateModeration={() => {
                  setSelectedResultObject({
                    ...currentItem,
                    removeCoordinates: true,
                  });
                  setActivateModeration(false);
                  showModal('#confirmation-remove-item-modal');
                }}
              />
            );
          })}
        </div>
      )}
      <ResultImageModal
        t={t}
        vehiculeCheckId={vehiculeCheckResult?.vehiculeCheckId}
        checkPointId={checkPointId}
        activeTab={activeTab}
        actionState={actionRef.current}
        videoRef={videoRef}
        cropConfigs={cropConfigs}
        selectedResultObject={
          actionRef.current === ResultActionState.EDIT
            ? selectedResultObject
            : undefined
        }
        vehiculeCheckResult={vehiculeCheckResult}
        onSelectEquipment={onSelectEquipment}
        onSelectDamage={onSelectDamage}
        setCropConfigs={setCropConfigs}
        setCompletedCrop={setCompletedCrop}
        getCroppedImage={getCroppedImage}
        mutateUploadImage={mutateUploadImage}
        moderationStatus={moderationStatus}
        canModerate={isModerator || isAdmin || isGroupManager}
        mutateUpdateImage={mutateUpdateImage}
      />
      <ConfirmationModal
        id="confirmation-remove-item-modal"
        title={t('common:confirmYourAction')}
        description={`${
          activateModeration
            ? t('areYourSureToDeleteThisModeration')
            : selectedResultObject?.['deactivateAllImages']
            ? t('areYourSureToDeleteTheseImages')
            : t('areYourSureToDeleteThisImage')
        } ?`}
        onConfirmLabel={t('common:delete')}
        onConfirm={() => {
          onClickDeactivate();
        }}
      />
      <div>
        {(() => {
          const activeImages =
            selectedResultObject?.images?.filter(
              (image) => !image?.isDeleted
            ) || [];

          const {
            containsImageList,
            containsMoreThanOneImage,
            currentImageIndex,
            currentImageUrl,
            onLeftClick,
            onRightClick,
            onDotClick,
          } = getImageNavigationHandlers({
            itemId: selectedResultObject?.id,
            images: activeImages,
            singleImageUrl:
              selectedResultObject?.customImage || selectedResultObject?.image,
          });

          // TODO: Uncomment this after temporarily hiding moderation buttons for ImageList
          const currentCoordinatesModerated = containsImageList
            ? activeImages?.[currentImageIndex]?.coordinatesModerated
            : selectedResultObject?.coordinatesModerated;
          // const currentCoordinatesModerated = containsImageList
          //   ? undefined
          //   : selectedResultObject?.coordinatesModerated;

          return (
            <>
              <ImagePreviewModal
                id={imagePreviewModalId || 'result-photo-preview-modal'}
                isOpen={isImagePreviewModalOpen}
                onOpen={() => setIsImagePreviewModalOpen(true)}
                onClose={() => setIsImagePreviewModalOpen(false)}
                onClickOutsideContent={() => setIsImagePreviewModalOpen(false)}
                onEscapePress={() => setIsImagePreviewModalOpen(false)}
              >
                <>
                  {(() => {
                    return (
                      <ModerationReportCanvas
                        // imageClassName="object-contain object-center"
                        imageClassName="max-h-screen min-w-[500px] object-contain object-center"
                        imgSrc={`${R2_PUBLIC_DOMAIN}/${
                          containsImageList
                            ? currentImageUrl || ''
                            : selectedResultObject?.customImage ||
                              selectedResultObject?.image ||
                              ''
                        }`}
                        onImageLoading={onCanvasImageLoading}
                        setElement={setElement}
                        setDmg={setDmg}
                        initialElement={currentCoordinatesModerated}
                        originalBoundingBox={
                          selectedResultObject?.originalBoundingBox
                        }
                        selectedResultObject={selectedResultObject}
                        mode={
                          activeTab === ResultPhotoTab.EQUIPMENTS
                            ? MODE_DRAW.RECTANGLE
                            : MODE_DRAW.POLYGONE
                        }
                      />
                    );
                  })()}
                </>
              </ImagePreviewModal>
              {containsMoreThanOneImage && isImagePreviewModalOpen && (
                <ImageNavigations
                  arrowClassName="fixed w-[45px] h-[45px] z-[100]"
                  leftArrowClassName="left-10"
                  rightArrowClassName="right-10"
                  arrowIconClassName="text-[18px]"
                  dotContainerClassName="fixed bottom-3.5 z-[100]"
                  images={activeImages}
                  activeImageId={activeImages?.[currentImageIndex]?.id}
                  disabled={canvasImageLoading}
                  onLeftClick={onLeftClick}
                  onRightClick={onRightClick}
                  onDotClick={onDotClick}
                />
              )}
            </>
          );
        })()}
      </div>
      {(isPendingUploadImage || isPendingUpdateImage) && <Loader />}
    </>
  );
};
